<template>
  <LLayerGroup>
    <!-- Detailed mode: Full featured markers with popups -->
    <template v-if="markerType === 'xl'">
      <LMarker
        v-for="location in locations"
        :key="location.id"
        :lat-lng="location.lngLat"
        :draggable="false"
        v-if="location.lngLat && location.lngLat.lat && location.lngLat.lng"
        @click="onMarkerClick(location)"
      >
        <MarkerPin
          :backgroundColor="legendLookup(location.value).backgroundColor"
          :color="legendLookup(location.value).color"
        >
          <i
            v-if="
              legendLookup(location.value).symbol &&
              legendLookup(location.value).symbol.type
            "
            :class="legendLookup(location.value).symbol.type"
            :style="{
              color: legendLookup(location.value).color,
            }"
          ></i>
          <span
            v-else-if="
              legendLookup(location.value).symbol &&
              legendLookup(location.value).symbol.text
            "
            :style="{
              color: legendLookup(location.value).color,
              fontSize: legendLookup(location.value).symbol.size + 'px',
            }"
            >{{ legendLookup(location.value).symbol.text }}</span
          >
        </MarkerPin>
      </LMarker>
    </template>
    <!-- Simplified mode: Use SVG renderer for better click interactivity -->
    <LGeoJson
      v-if="markerType !== 'xl'"
      :geojson="geoJsonData"
      :options="geoJsonOptions"
      ref="geoJsonLayer"
    />
    <LPopup
      v-if="selectedLocation"
      ref="popup"
      :lat-lng="selectedLocation.lngLat"
      @update:visible="onPopupClose"
      :key="`popup-${selectedLocationId}`"
    >
      <LocationPopupContent :location="selectedLocation" />
    </LPopup>
  </LLayerGroup>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import { LMarker, LPopup, LLayerGroup, LGeoJson } from "vue2-leaflet";
import MarkerPin from "../../../common/map/MarkerPin.vue";
import * as L from "leaflet";
import LocationPopupContent from "./LocationPopupContent.vue";

export default {
  name: "LocationMarkers",

  components: {
    LMarker,
    LPopup,
    LLayerGroup,
    LGeoJson,
    MarkerPin,
    LocationPopupContent,
  },

  props: {
    locations: {
      type: Array,
      required: true,
      default: () => [],
    },
  },

  data() {
    return {
      selectedLocationId: null,
    };
  },

  computed: {
    ...mapGetters({
      markerType: "project/map/markerType",
      allLocations: "project/map/locations",
    }),
    /**
     * Get the selected location object from the current locations array
     * This ensures the popup stays open when recordType changes
     */
    selectedLocation() {
      if (!this.selectedLocationId) return null;
      return (
        this.allLocations.find((loc) => loc.id === this.selectedLocationId) || null
      );
    },

    legend() {
      return this.$store.getters["project/map/legend"];
    },
    legendLookup() {
      return this.$store.getters["project/map/legendLookup"];
    },
    /**
     * Convert locations to GeoJSON format for optimized rendering with observation count colors
     * @returns {Object} - GeoJSON FeatureCollection
     */
    geoJsonData() {
      if (!this.locations || !Array.isArray(this.locations)) {
        return { type: "FeatureCollection", features: [] };
      }

      const features = [];

      // Cache legend lookups for performance
      const colorCache = new Map();
      const legendLookupCache = (count) => {
        if (!colorCache.has(count)) {
          const range = this.legendLookup(count);
          colorCache.set(count, range);
        }
        return colorCache.get(count);
      };

      for (let i = 0; i < this.locations.length; i++) {
        const location = this.locations[i];
        if (!location.lngLat || !location.lngLat.lat || !location.lngLat.lng) {
          continue;
        }

        // Look up range from legend based on observation count (with caching)
        const colors = legendLookupCache(location.value);

        features.push({
          type: "Feature",
          properties: {
            id: location["@id"],
            locationId: location.id,
            name: location.name || "",
            observationsTotal: location.value,
            color: colors.color,
            backgroundColor: colors.backgroundColor,
          },
          geometry: {
            type: "Point",
            coordinates: [location.lngLat.lng, location.lngLat.lat],
          },
        });
      }

      return {
        type: "FeatureCollection",
        features,
      };
    },
    /**
     * GeoJSON layer options with SVG/Canvas renderer for better performance
     * Canvas is used for 1000+ markers for better rendering performance
     */
    geoJsonOptions() {
      const useCanvas =
        this.markerType === "xs" ||
        this.markerType === "sm" ||
        this.markerType === "md";

      // Create a Map for O(1) location lookups in click handler
      const locationMap = new Map();
      for (const location of this.locations) {
        locationMap.set(location.id, location);
      }

      return {
        // Use Canvas renderer for 1000+ markers (better performance)
        // Use SVG renderer for <= 1000 markers (better click interactivity)
        renderer: useCanvas
          ? L.canvas({ padding: 0.5 })
          : L.svg({ padding: 0.5 }),
        pointToLayer: (feature, latlng) => {
          const backgroundColor = feature.properties.backgroundColor;
          const color = feature.properties.color;

          if (this.markerType === "xs") {
            return L.circleMarker(latlng, {
              radius: 2,
              fillColor: backgroundColor,
              color: color,
              weight: 0,
              opacity: 1,
              fillOpacity: 1,
            });
          } else if (this.markerType === "sm") {
            return L.circleMarker(latlng, {
              radius: 4,
              fillColor: backgroundColor,
              color: color,
              weight: 0,
              opacity: 1,
              fillOpacity: 1,
            });
          } else if (this.markerType === "md") {
            return L.circleMarker(latlng, {
              radius: 6,
              fillColor: backgroundColor,
              color: color,
              weight: 1,
              opacity: 1,
              fillOpacity: 1,
            });
          } else if (this.markerType === "lg") {
            return L.circleMarker(latlng, {
              radius: 8,
              fillColor: backgroundColor,
              color: color,
              weight: 2,
              opacity: 1,
              fillOpacity: 1,
            });
          } else {
          }
        },
        onEachFeature: (feature, layer) => {
          layer.on("click", () => {
            // O(1) lookup using Map instead of O(n) find()
            const location = locationMap.get(feature.properties.locationId);
            if (location) {
              this.onMarkerClick(location);
            }
          });
        },
      };
    },
  },

  methods: {
    ...mapMutations({
      LOCATIONS_SET_MARKERS_IN_VIEW:
        "project/map/LOCATIONS_SET_MARKERS_IN_VIEW",
    }),
    onMarkerClick(location) {
      this.selectedLocationId = location.id;

      this.$nextTick(() => {
        if (
          this.$refs.popup &&
          this.$refs.popup.mapObject &&
          this.$refs.popup.mapObject._source
        ) {
          const map = this.$refs.popup.mapObject._source._map;
          if (map) {
            this.$refs.popup.mapObject.openOn(map);
          }
        }
      });
    },

    onPopupClose(visible) {
      if (!visible) {
        this.selectedLocationId = null;
      }
    },
  },
  watch: {
    locations: {
      handler() {
        // When locations change, re-pan to all
        this.LOCATIONS_SET_MARKERS_IN_VIEW(this.locations.length);
      },
      deep: false,
    },
  },
};
</script>
<style>
/* Override Leaflet's link styling for buttons in popups */
.leaflet-popup-content a.btn {
  color: #fff !important;
  text-decoration: none !important;
}

.leaflet-popup-content .btn-primary {
  color: #fff !important;
}
</style>
